import React, { useEffect, useState } from 'react';
import { Button, Col, DatePicker, Form, Input, Modal, Row, Select, Space } from 'antd';

import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

import { DAY_NAME, TIME_FORMAT } from '../../constants';
import { capitalize } from '../../utils/common';
import TimesForDay from '../Forms/Companies/TimesForDay';
import moment from 'moment';
import './OffSchedule.scss';
import { useDispatch } from 'react-redux';
import { doUpdateLocation } from '../../modules/locations/stores/thunk';
import { useSelector } from 'react-redux';
import { doCreateOffSchedule, doGetOffSchedule, doUpdateOffSchedule } from '../../modules/off-schedules/stores/thunk';
import dayjs from 'dayjs';

const initHoursData = {
  start: null,
  end: null,
  isValid: true,
};
const dayOfWeeks = Object.keys(DAY_NAME);
const initPickupHours = {};
const initPickupHoursData = {};
dayOfWeeks?.forEach((day) => {
  initPickupHours[day] = false;
  initPickupHoursData[day] = initHoursData;
});

export const OffSchedule = ({ open, close, location, deliveryArea }) => {
  const { offSchedule } = useSelector((state) => state.offSchedule);
  const [pickupHours, setPickupHours] = useState(initPickupHours);
  const [pickupHoursData, setPickupHoursData] = useState(initPickupHoursData);

  const [form] = Form.useForm();
  const dispatch = useDispatch();

  useEffect(() => {
    form.resetFields();
    if (offSchedule?.namedDates) {
      form.setFieldValue(
        'holidays',
        offSchedule.namedDates.map((nd) => ({ id: nd.id, name: nd.name, date: dayjs(nd.date) })),
      );
    }
    if (offSchedule?.weekDays) {
      form.setFieldValue('weekDays', offSchedule.weekDays);
    }
  }, [offSchedule]);

  useEffect(() => {
    if (location || deliveryArea) {
      const params = { locationId: location?.id, deliveryAreaId: deliveryArea?.id };
      dispatch(doGetOffSchedule(params));
    }
    if (location) {
      let pickupHours = {};
      Object.keys(DAY_NAME)?.forEach((day) => {
        const key = DAY_NAME[day].toLowerCase();
        const start = location[key + 'From'];
        const end = location[key + 'To'];
        pickupHours[day] = {
          start,
          end,
          isValid: true,
        };
      });
      const newPickupHours = {};

      dayOfWeeks?.forEach((day) => {
        const start = pickupHours[day].start;
        const end = pickupHours[day].end;
        newPickupHours[day] = !!start || !!end;
      });

      setPickupHoursData(pickupHours);
      setPickupHours(newPickupHours);
    }
  }, [location, deliveryArea]);

  const onClickPickupHour = (key) => {
    const preValue = pickupHours[key];
    const newPickupHours = { ...pickupHours };
    newPickupHours[key] = !pickupHours[key];
    if (preValue) {
      const newPickupHoursData = {
        ...pickupHoursData,
        [key]: initHoursData,
      };
      setPickupHoursData(newPickupHoursData);
    }
    setPickupHours(newPickupHours);
  };
  const changeTimeForHour = (key, type, value) => {
    if (!pickupHoursData[key]) return;

    const newPickupHoursData = {
      ...pickupHoursData,
      [key]: {
        ...pickupHoursData[key],
        [type]: value?.format('HH:mm') || null,
      },
    };

    if (newPickupHoursData[key].start && newPickupHoursData[key].end) {
      const start = newPickupHoursData[key].start;
      const end = newPickupHoursData[key].end;
      newPickupHoursData[key]['isValid'] = moment(start, TIME_FORMAT).isBefore(moment(end, TIME_FORMAT));
    } else {
      newPickupHoursData[key][' '] = true;
    }

    setPickupHoursData(newPickupHoursData);
  };
  const onFinish = async (values) => {
    if (location) {
      const params = {
        locationId: location.locationId,
        companyId: location.companyId,
        mondayFrom: pickupHoursData?.mon?.start || null,
        mondayTo: pickupHoursData?.mon?.end || null,
        tuesdayFrom: pickupHoursData?.tue?.start || null,
        tuesdayTo: pickupHoursData?.tue?.end || null,
        wednesdayFrom: pickupHoursData?.wed?.start || null,
        wednesdayTo: pickupHoursData?.wed?.end || null,
        thursdayFrom: pickupHoursData?.thu?.start || null,
        thursdayTo: pickupHoursData?.thu?.end || null,
        fridayFrom: pickupHoursData?.fri?.start || null,
        fridayTo: pickupHoursData?.fri?.end || null,
        saturdayFrom: pickupHoursData?.sat?.start || null,
        saturdayTo: pickupHoursData?.sat?.end || null,
        sundayFrom: pickupHoursData?.sun?.start || null,
        sundayTo: pickupHoursData?.sun?.end || null,
      };

      await dispatch(doUpdateLocation(params));
    }

    const offScheduleParams = {
      locationId: location?.id || undefined,
      deliveryAreaId: deliveryArea?.id || undefined,
      weekDays: values.weekDays || [],
      namedDates: values.holidays,
    };

    if (offSchedule?.id) {
      // update
      offScheduleParams.id = offSchedule.id;
      dispatch(doUpdateOffSchedule(offScheduleParams));
    } else {
      // create
      await dispatch(doCreateOffSchedule(offScheduleParams));
    }
    close();
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((d) => ({
    label: capitalize(d),
    value: d,
  }));

  return (
    <>
      <Modal
        width={600}
        closeIcon={false}
        title={`Define calendar for ${
          location ? `location: ${location?.name}` : `delivery area: ${deliveryArea?.name}`
        }`}
        open={open}
        forceRender
      >
        <>
          <div className="LocationForm">
            <Form
              form={form}
              scrollToFirstError
              onFinish={onFinish}
              layout="vertical"
              autoComplete="off"
              style={{ marginTop: 16 }}
            >
              {location && (
                <Row>
                  <Col span={24}>
                    <div className="pickupHours">
                      <label>Pickup Hours</label>
                      <div className="hours-content">
                        {dayOfWeeks?.map((day) => (
                          <Button
                            key={'check' + day}
                            className={`content ${pickupHours[day] ? 'selected' : ''}`}
                            onClick={() => onClickPickupHour([day])}
                          >
                            {capitalize(day)}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </Col>
                  {dayOfWeeks
                    .filter((day) => pickupHours[day])
                    ?.map((day) => (
                      <Col span={24} key={day + 'data'}>
                        <TimesForDay
                          title={DAY_NAME[day]}
                          onChangeStartTime={(value) => changeTimeForHour(day, 'start', value)}
                          onChangeEndTime={(value) => changeTimeForHour(day, 'end', value)}
                          isValid={pickupHoursData[day].isValid}
                          start={pickupHoursData[day].start}
                          end={pickupHoursData[day].end}
                        />
                      </Col>
                    ))}
                </Row>
              )}

              {deliveryArea && (
                <>
                  <div style={{ marginTop: 16 }}>
                    <label style={{ fontWeight: 'bold' }}>Regular off days</label>
                    <p>Define days that are generally not working days in the week</p>
                  </div>
                  <Form.Item name="weekDays" initialValue={offSchedule?.weekdays}>
                    <Select
                      mode="multiple"
                      allowClear
                      style={{ width: '100%' }}
                      placeholder="Please select"
                      options={days}
                    />
                  </Form.Item>
                </>
              )}

              <div style={{ marginTop: 16 }}>
                <label style={{ fontWeight: 'bold' }}>Holidays</label>
              </div>

              <Form.List name="holidays">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Space key={key} style={{ display: 'flex' }} align="baseline">
                        <Form.Item
                          {...restField}
                          name={[name, 'name']}
                          rules={[{ required: true, message: 'Missing name' }]}
                        >
                          <Input placeholder="X-mas" size="large" style={{ width: 300 }} />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'date']}
                          rules={[{ required: true, message: 'Missing date' }]}
                        >
                          <DatePicker size="large" style={{ width: 200 }} />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(name)} />
                      </Space>
                    ))}
                    <Form.Item>
                      <Button
                        type="dashed"
                        style={{ marginTop: 16 }}
                        // size="small"
                        onClick={() => add()}
                        icon={<PlusOutlined />}
                      >
                        Add holiday
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="default" onClick={close}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  Save holidays
                </Button>
              </div>
            </Form>
          </div>
        </>
      </Modal>
    </>
  );
};
